<template>
  <div
    role="main"
    aria-label="Tableau des expéditeurs logistiques"
  >
    <!-- Summary Statistics Cards -->
    <b-row
      class="mb-3"
      aria-label="Statistiques des expéditeurs"
    >
      <b-col
        lg="3"
        md="6"
        sm="12"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h6 class="font-weight-bold mb-1">
                Total Expéditeurs
              </h6>
              <h3 class="font-weight-bolder mb-0">
                {{ summaryStats.total || 0 }}
              </h3>
            </div>
            <div class="avatar bg-light-primary p-2">
              <feather-icon
                icon="UsersIcon"
                class="text-primary"
                size="18"
              />
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        lg="3"
        md="6"
        sm="12"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h6 class="font-weight-bold mb-1">
                Expéditeurs Actifs
              </h6>
              <h3 class="font-weight-bolder mb-0 text-success">
                {{ summaryStats.active || 0 }}
              </h3>
            </div>
            <div class="avatar bg-light-success p-2">
              <feather-icon
                icon="CheckCircleIcon"
                class="text-success"
                size="18"
              />
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        lg="3"
        md="6"
        sm="12"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h6 class="font-weight-bold mb-1">
                Expéditeurs Inactifs
              </h6>
              <h3 class="font-weight-bolder mb-0 text-secondary">
                {{ summaryStats.inactive || 0 }}
              </h3>
            </div>
            <div class="avatar bg-light-secondary p-2">
              <feather-icon
                icon="XCircleIcon"
                class="text-secondary"
                size="18"
              />
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        lg="3"
        md="6"
        sm="12"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h6 class="font-weight-bold mb-1">
                Prix par poids
              </h6>
              <h3 class="font-weight-bolder mb-0 text-info">
                {{ summaryStats.weightPricing || 0 }}
              </h3>
            </div>
            <div class="avatar bg-light-info p-2">
              <feather-icon
                icon="TrendingUpIcon"
                class="text-info"
                size="18"
              />
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Search and Actions Bar -->
    <b-card
      class="mb-3"
      aria-label="Barre de recherche et filtres"
    >
      <b-row>
        <!-- Search Field -->
        <b-col
          md="3"
          sm="6"
        >
          <b-form-group label-class="form-label-premium">
            <label for="search-input">Recherche <span class="sr-only">(par nom, email, etc.)</span></label>
            <b-input-group>
              <b-form-input class="premium-input"
                id="search-input"
                v-model="searchTerm"
                placeholder="Rechercher par nom, email..."
                aria-label="Recherche de fournisseur"
                aria-describedby="search-help"
                @input="onSearchChange"
              />
              <b-input-group-append>
                <b-button
                  variant="primary"
                  aria-label="Lancer la recherche"
                  @click="getSuppliers"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="16"
                  />
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-form-text
              id="search-help"
              class="sr-only"
            >
              Saisissez un nom, un email ou un critère pour filtrer les expéditeurs.
            </b-form-text>
          </b-form-group>
        </b-col>

        <!-- Repository Filter: Only show for admin, not manager -->
        <b-col
          v-if="user.role === 'admin'"
          md="3"
          sm="6"
        >
          <b-form-group label-class="form-label-premium">
            <label for="repository-filter">Dépôt</label>
            <v-select
              id="repository-filter"
              v-model="filters.repository"
              :options="repositories"
              label="name"
              placeholder="Tous les dépôts"
              :reduce="repo => repo.id"
              clearable
              class="enhanced-select border-select"
              aria-label="Filtrer par dépôt logistique"
              aria-describedby="repository-filter-help"
              @input="getSuppliers"
            />
            <b-form-text
              id="repository-filter-help"
              class="sr-only"
            >
              Sélectionnez un dépôt logistique pour filtrer la liste.
            </b-form-text>
          </b-form-group>
        </b-col>

        <!-- Status Filter -->
        <b-col
          md="3"
          sm="6"
        >
          <b-form-group label-class="form-label-premium">
            <label for="status-filter">Statut</label>
            <v-select
              id="status-filter"
              v-model="filters.is_active"
              :options="[
                {label: 'Tous', value: null},
                {label: 'Actif', value: true},
                {label: 'Inactif', value: false}
              ]"
              label="label"
              :reduce="item => item.value"
              placeholder="Tous"
              clearable
              class="enhanced-select border-select"
              aria-label="Filtrer par statut du fournisseur"
              aria-describedby="status-filter-help"
              @input="getSuppliers"
            />
            <b-form-text
              id="status-filter-help"
              class="sr-only"
            >
              Filtrez les expéditeurs par statut (actif ou inactif).
            </b-form-text>
          </b-form-group>
        </b-col>

        <!-- Price Type Filter -->
        <b-col
          md="3"
          sm="6"
        >
          <b-form-group label-class="form-label-premium">
            <label for="price-type-filter">Type de prix</label>
            <v-select
              id="price-type-filter"
              v-model="filters.weight_price"
              :options="[
                {label: 'Tous', value: null},
                {label: 'Prix par poids', value: true},
                {label: 'Prix fixe', value: false}
              ]"
              label="label"
              :reduce="item => item.value"
              placeholder="Tous"
              clearable
              class="enhanced-select border-select"
              aria-label="Filtrer par type de tarification"
              aria-describedby="price-type-filter-help"
              @input="getSuppliers"
            />
            <b-form-text
              id="price-type-filter-help"
              class="sr-only"
            >
              Filtrez les expéditeurs selon le type de tarification (prix par poids ou prix fixe).
            </b-form-text>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Second Row of Filters -->
      <b-row v-if="['admin', 'superadmin', 'sales'].includes(user.role)">
        <!-- Payment Days Configuration Filter -->
        <b-col
          md="3"
          sm="6"
        >
          <b-form-group label-class="form-label-premium">
            <label for="payment-days-filter">
              <feather-icon
                icon="CalendarIcon"
                size="14"
                class="mr-50"
              />
              Jours de paiement
            </label>
            <v-select
              id="payment-days-filter"
              v-model="filters.has_payment_days"
              :options="[
                {label: 'Tous', value: null},
                {label: 'Configurés', value: true},
                {label: 'Non configurés', value: false}
              ]"
              label="label"
              :reduce="item => item.value"
              placeholder="Tous"
              clearable
              class="enhanced-select border-select"
              aria-label="Filtrer par configuration des jours de paiement"
              @input="getSuppliers"
            />
            <small class="text-muted">
              Filtrer selon la configuration des jours de paiement
            </small>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Advanced Filters -->
      <div
        v-if="showAdvancedFilters"
        class="mt-2"
      >
        <b-row>
          <!-- Price Range Filters -->
          <b-col
            md="3"
            sm="6"
          >
            <b-form-group label-class="form-label-premium" label="Prix livraison min">
              <b-input-group>
                <b-form-input class="premium-input"
                  v-model="filters.min_price_delivery"
                  type="number"
                  step="0.001"
                  placeholder="Min"
                />
                <b-input-group-append is-text>
                  <span>TND</span>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            sm="6"
          >
            <b-form-group label-class="form-label-premium" label="Prix livraison max">
              <b-input-group>
                <b-form-input class="premium-input"
                  v-model="filters.max_price_delivery"
                  type="number"
                  step="0.001"
                  placeholder="Max"
                />
                <b-input-group-append is-text>
                  <span>TND</span>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>

          <!-- Date Range Filters -->
          <b-col
            md="3"
            sm="6"
          >
            <b-form-group label-class="form-label-premium" label="Date création depuis">
              <b-form-datepicker class="premium-input"
                v-model="filters.created_at_after"
                placeholder="Depuis"
                locale="fr"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            sm="6"
          >
            <b-form-group label-class="form-label-premium" label="Date création jusqu'à">
              <b-form-datepicker class="premium-input"
                v-model="filters.created_at_before"
                placeholder="Jusqu'à"
                locale="fr"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-between mt-3">
        <div>
          <b-button
            v-b-tooltip.hover
            title="Filtres avancés"
            variant="outline-secondary"
            class="mr-1"
            @click="toggleAdvancedFilters"
          >
            <feather-icon
              :icon="showAdvancedFilters ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              size="16"
              class="mr-1"
            />
            <span>Filtres avancés</span>
          </b-button>

          <b-button
            v-b-tooltip.hover
            title="Réinitialiser les filtres"
            variant="outline-secondary"
            class="mr-1"
            @click="resetFilters"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
            />
          </b-button>
        </div>

        <div>
          <b-button
            v-b-tooltip.hover
            title="Vue Carte/Liste"
            :variant="viewMode === 'table' ? 'outline-secondary' : 'outline-primary'"
            class="mr-1"
            @click="toggleViewMode"
          >
            <feather-icon
              :icon="viewMode === 'table' ? 'GridIcon' : 'ListIcon'"
              size="16"
            />
          </b-button>

          <b-button
            v-b-tooltip.hover
            title="Exporter en CSV"
            variant="outline-success"
            class="mr-1"
            @click="exportToCSV"
          >
            <feather-icon
              icon="DownloadIcon"
              size="16"
            />
          </b-button>

          <b-button
            v-if="user && (user.role === 'admin' || user.role === 'sales')"
            :to="{ name: 'supplier-add' }"
            variant="primary"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
            />
            <span class="ml-1">Ajouter Expéditeur</span>
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="text-center mb-2"
    >
      <b-spinner variant="primary" />
    </div>

    <!-- Card View -->
    <div v-else-if="viewMode === 'card' && !isLoading">
      <b-row>
        <b-col
          v-for="supplier in suppliers"
          :key="supplier.id"
          lg="4"
          md="6"
          sm="12"
          class="mb-3"
        >
          <b-card
            no-body
            class="supplier-card h-100"
            :class="{ 'border-success': supplier.is_active, 'border-secondary': !supplier.is_active }"
          >
            <b-card-body>
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h5
                  class="mb-0"
                  :class="supplier.is_active ? 'text-dark' : 'text-muted'"
                >
                  {{ supplier.username || 'Sans nom' }}
                </h5>
                <b-badge
                  pill
                  :variant="supplier.is_active ? 'success' : 'secondary'"
                  class="supplier-status-badge"
                >
                  <feather-icon
                    :icon="supplier.is_active ? 'CheckCircleIcon' : 'XCircleIcon'"
                    size="10"
                    class="mr-1"
                  />
                  {{ supplier.is_active ? 'Actif' : 'Inactif' }}
                </b-badge>
              </div>

              <div class="supplier-info mb-3">
                <p class="mb-1">
                  <feather-icon
                    icon="MailIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ supplier.email || 'N/A' }}
                </p>
                <p class="mb-1">
                  <feather-icon
                    icon="PhoneIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ supplier.phone || 'N/A' }}
                </p>
                <p class="mb-1">
                  <feather-icon
                    icon="HomeIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ typeof supplier.repository === 'string' ? supplier.repository :
                    (typeof supplier.repository === 'object' && supplier.repository ? supplier.repository.name : 'N/A') }}
                </p>
              </div>

              <div class="invoice-days-summary mb-2">
                <div class="invoice-days-title">
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                    class="mr-50"
                  />
                  Jours paiements
                </div>
                <div
                  v-if="getInvoiceGenerationDays(supplier).length"
                  class="invoice-days-list"
                >
                  <span
                    v-for="day in getInvoiceGenerationDays(supplier)"
                    :key="`${supplier.id}-${day}`"
                    :class="['invoice-day-chip', getDayChipClass(day)]"
                  >
                    <feather-icon
                      icon="CheckCircleIcon"
                      size="11"
                      class="mr-25"
                    />
                    {{ getShortDayLabel(day) }}
                  </span>
                </div>
                <div
                  v-else
                  class="invoice-days-empty-state"
                >
                  <b-badge
                    variant="light-warning"
                    class="payment-days-warning"
                  >
                    <feather-icon
                      icon="AlertTriangleIcon"
                      size="12"
                      class="mr-50"
                    />
                    Non configuré
                  </b-badge>
                </div>
              </div>

              <div class="d-flex justify-content-between align-items-center">
                <div class="pricing-summary">
                  <small class="text-muted d-block">Livraison: <strong>{{ supplier.price_delivery || 0 }} DT</strong></small>
                  <small class="text-muted d-block">Retour: <strong>{{ supplier.price_return || 0 }} DT</strong></small>
                  <b-badge
                    v-if="supplier.weight_price"
                    variant="light-info"
                    class="mt-1"
                  >
                    <feather-icon
                      icon="TrendingUpIcon"
                      size="12"
                      class="mr-1"
                    />
                    Prix par poids
                  </b-badge>
                </div>

                <div>
                  <b-button-group size="sm">
                    <b-button
                      v-b-tooltip.hover
                      title="Voir détails"
                      variant="outline-primary"
                      :to="{ name: 'supplier-show', params: { id: supplier.id }}"
                    >
                      <feather-icon
                        icon="EyeIcon"
                        size="14"
                      />
                    </b-button>

                    <b-button
                      v-if="user && ['admin', 'superadmin', 'sales'].includes(user.role)"
                      v-b-tooltip.hover
                      title="Configurer jours de paiement"
                      variant="outline-info"
                      @click="openPaymentDaysModal(supplier)"
                    >
                      <feather-icon
                        icon="CalendarIcon"
                        size="14"
                      />
                    </b-button>

                    <b-button
                      v-if="user && user.role === 'admin'"
                      v-b-tooltip.hover
                      title="Changer le mot de passe"
                      variant="outline-warning"
                      @click="openPasswordChangeModal(supplier)"
                    >
                      <feather-icon
                        icon="LockIcon"
                        size="14"
                      />
                    </b-button>

                    <b-button
                      v-if="user && (user.role === 'admin' || user.role === 'sales')"
                      v-b-tooltip.hover
                      title="Modifier"
                      variant="outline-secondary"
                      :to="{ name: 'supplier-edit', params: { id: supplier.id }}"
                    >
                      <feather-icon
                        icon="EditIcon"
                        size="14"
                      />
                    </b-button>

                    <b-button
                      v-if="user && user.role === 'admin'"
                      v-b-tooltip.hover
                      :title="supplier.is_active ? 'Désactiver' : 'Activer'"
                      :variant="supplier.is_active ? 'outline-warning' : 'outline-success'"
                      @click="toggleSupplierStatus(supplier)"
                    >
                      <feather-icon
                        :icon="supplier.is_active ? 'PowerIcon' : 'CheckCircleIcon'"
                        size="14"
                      />
                    </b-button>

                    <span
                      v-if="user && (user.role === 'admin' || user.role === 'superadmin')"
                      v-b-tooltip.hover
                      :title="supplier.can_be_deleted !== false ? 'Supprimer' : (supplier.deletion_block_reason || 'Impossible de supprimer ce fournisseur')"
                      class="d-inline-block"
                    >
                      <b-button
                        variant="outline-danger"
                        :disabled="supplier.can_be_deleted === false"
                        :class="{ 'cursor-not-allowed not-allowed': supplier.can_be_deleted === false }"
                        @click="confirmSafeDelete(supplier)"
                      >
                        <feather-icon
                          icon="Trash2Icon"
                          size="14"
                        />
                      </b-button>
                    </span>
                  </b-button-group>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- No items found -->
    <b-card
      v-if="!isLoading && suppliers.length === 0"
      class="text-center py-5"
    >
      <div class="empty-state-icon mb-3">
        <feather-icon
          icon="UsersIcon"
          size="60"
          class="text-muted opacity-25"
        />
      </div>
      <h4>Aucun fournisseur trouvé</h4>
      <p class="text-muted">
        Ajoutez des expéditeurs ou modifiez vos critères de recherche
      </p>
    </b-card>

    <!-- Password Change Modal -->
    <b-modal
      id="password-change-modal"
      v-model="showPasswordChangeModal"
      centered
      hide-footer
      size="lg"
      title-tag="div"
      @hidden="resetPasswordChangeModal"
    >
      <template #modal-header="{ close }">
        <div class="w-100">
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <div class="password-icon-wrapper-modal mr-2">
                <feather-icon
                  icon="LockIcon"
                  size="20"
                  class="text-primary"
                />
              </div>
              <div>
                <h5 class="mb-0">
                  Changer le mot de passe
                </h5>
                <small
                  v-if="selectedSupplierForPassword"
                  class="text-muted"
                >
                  {{ selectedSupplierForPassword.username }} • {{ selectedSupplierForPassword.email }}
                </small>
              </div>
            </div>
            <b-button
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              @click="close"
            >
              <feather-icon icon="XIcon" />
            </b-button>
          </div>
        </div>
      </template>

      <div v-if="selectedSupplierForPassword">
        <validation-observer
          ref="passwordFormValidator"
          v-slot="{ handleSubmit }"
        >
          <b-form @submit.prevent="handleSubmit(savePasswordChange)">
            <!-- Password Input -->
            <b-row>
              <b-col cols="12">
                <b-form-group
                  label="Nouveau mot de passe *"
                  label-for="modal-password"
                  label-class="form-label-premium"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Mot de passe"
                    rules="required|min:8"
                    vid="modal-password"
                  >
                    <b-input-group class="premium-input-group">
                      <b-form-input
                        id="modal-password"
                        v-model="modalPasswordForm.password"
                        :type="showModalPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : (passwordStrength > 25 ? true : null)"
                        placeholder="Entrez un mot de passe sécurisé"
                        class="premium-input"
                        @input="onPasswordInput"
                      />
                      <b-input-group-append>
                        <b-button
                          variant="outline-secondary"
                          class="password-toggle-btn"
                          @click="showModalPassword = !showModalPassword"
                        >
                          <feather-icon
                            :icon="showModalPassword ? 'EyeOffIcon' : 'EyeIcon'"
                            size="16"
                          />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>

                    <!-- Password Strength Indicator -->
                    <div
                      v-if="modalPasswordForm.password"
                      class="password-strength-wrapper mt-2"
                    >
                      <div class="d-flex justify-content-between mb-1">
                        <small class="strength-label">Force du mot de passe :</small>
                        <small
                          class="strength-text"
                          :class="passwordStrengthClass"
                        >{{ passwordStrengthText }}</small>
                      </div>
                      <b-progress
                        :value="passwordStrength"
                        :variant="passwordStrengthClass"
                        max="100"
                        height="6px"
                        class="premium-progress"
                      />
                    </div>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Confirm Password Input -->
              <b-col cols="12">
                <b-form-group
                  label="Confirmer le mot de passe *"
                  label-for="modal-confirm-password"
                  label-class="form-label-premium"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Confirmation du mot de passe"
                    rules="required|confirmed:modal-password"
                  >
                    <b-input-group class="premium-input-group">
                      <b-form-input
                        id="modal-confirm-password"
                        v-model="modalPasswordForm.confirm_password"
                        :type="showModalConfirmPassword ? 'text' : 'password'"
                        :state="getConfirmPasswordState(errors)"
                        placeholder="Confirmez le mot de passe"
                        class="premium-input"
                        @input="onPasswordInput"
                      />
                      <b-input-group-append>
                        <b-button
                          variant="outline-secondary"
                          class="password-toggle-btn"
                          @click="showModalConfirmPassword = !showModalConfirmPassword"
                        >
                          <feather-icon
                            :icon="showModalConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                            size="16"
                          />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>

                    <!-- Real-time validation feedback -->
                    <div
                      v-if="modalPasswordForm.confirm_password"
                      class="mt-1"
                    >
                      <small
                        v-if="passwordsMatch && modalPasswordForm.password"
                        class="text-success d-flex align-items-center validation-message-animate"
                      >
                        <feather-icon
                          icon="CheckCircleIcon"
                          size="14"
                          class="mr-50"
                        />
                        Les mots de passe correspondent
                      </small>
                      <small
                        v-else-if="modalPasswordForm.password"
                        class="text-danger d-flex align-items-center validation-message-animate"
                      >
                        <feather-icon
                          icon="XCircleIcon"
                          size="14"
                          class="mr-50"
                        />
                        Les mots de passe ne correspondent pas
                      </small>
                      <small
                        v-else
                        class="text-warning d-flex align-items-center validation-message-animate"
                      >
                        <feather-icon
                          icon="AlertTriangleIcon"
                          size="14"
                          class="mr-50"
                        />
                        Veuillez d'abord saisir un mot de passe
                      </small>
                    </div>
                    <small
                      v-if="errors.length > 0 && !modalPasswordForm.confirm_password"
                      class="text-danger"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Generate Password Button -->
            <div class="text-center mb-3 mt-2">
              <PasswordGenerator
                :user="selectedSupplierForPassword"
                user-type="supplier"
                @generated="onPasswordGenerated"
              />
            </div>

            <!-- Action Buttons -->
            <div class="d-flex justify-content-between mt-4">
              <b-button
                variant="outline-secondary"
                class="premium-btn-secondary"
                @click="closePasswordChangeModal"
              >
                <feather-icon
                  icon="XIcon"
                  size="16"
                  class="mr-50"
                />
                Annuler
              </b-button>
              <span id="password-save-btn-wrapper" class="d-inline-block">
                <b-button
                  type="submit"
                  variant="primary"
                  class="premium-btn-primary save-btn-animated"
                  :class="{ 'btn-saving': isSavingPassword, 'not-allowed': !canSavePassword || isSavingPassword }"
                  :disabled="!canSavePassword || isSavingPassword"
                >
                  <b-spinner
                    v-if="isSavingPassword"
                    small
                    class="mr-50"
                  />
                  <feather-icon
                    v-else
                    icon="SaveIcon"
                    size="16"
                    class="mr-50"
                  />
                  Enregistrer
                </b-button>
                <b-tooltip
                  v-if="!canSavePassword || isSavingPassword"
                  target="password-save-btn-wrapper"
                  triggers="hover focus"
                  placement="top"
                >
                  <template v-if="isSavingPassword">
                    Enregistrement en cours...
                  </template>
                  <template v-else-if="modalPasswordForm.password && modalPasswordForm.confirm_password && !passwordsMatch">
                    Les mots de passe ne correspondent pas.
                  </template>
                  <template v-else-if="modalPasswordForm.password && modalPasswordForm.password.length < 8">
                    Le mot de passe doit comporter au moins 8 caractères.
                  </template>
                  <template v-else-if="!modalPasswordForm.password || !modalPasswordForm.confirm_password">
                    Veuillez remplir les champs mot de passe et confirmation.
                  </template>
                  <template v-else>
                    Veuillez remplir correctement les champs.
                  </template>
                </b-tooltip>
              </span>
            </div>
          </b-form>
        </validation-observer>
      </div>
    </b-modal>

    <!-- Quick Edit Payment Days Modal -->
    <b-modal
      id="payment-days-modal"
      v-model="showPaymentDaysModal"
      centered
      hide-footer
      size="lg"
      @hidden="resetPaymentDaysModal"
    >
      <template #modal-header="{ close }">
        <div class="w-100">
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <div class="payment-icon-wrapper-modal mr-2">
                <feather-icon
                  icon="CalendarIcon"
                  size="20"
                  class="text-primary"
                />
              </div>
              <div>
                <h5 class="mb-0">
                  Configuration des jours de paiement
                </h5>
                <small
                  v-if="selectedSupplier"
                  class="text-muted"
                >
                  {{ selectedSupplier.username }} • {{ selectedSupplier.email }}
                </small>
              </div>
            </div>
            <b-button
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              @click="close"
            >
              <feather-icon icon="XIcon" />
            </b-button>
          </div>
        </div>
      </template>

      <div v-if="selectedSupplier">
        <b-alert
          variant="info"
          show
          class="mb-3 d-flex align-items-center"
        >
          <feather-icon
            icon="InfoIcon"
            size="16"
            class="mr-2"
          />
          <small>
            Cliquez sur les jours de la semaine prévus pour le règlement et la génération automatique des factures.
          </small>
        </b-alert>

        <!-- Visual Day Picker Grid -->
        <div class="payment-days-section-modal mb-3">
          <div class="days-picker-grid">
            <div
              v-for="day in weekdayOptions"
              :key="day.value"
              :class="[
                'day-picker-item',
                { 'selected': isModalDaySelected(day.value) }
              ]"
              @click="toggleModalDay(day.value)"
            >
              <div class="day-icon mb-1">
                <feather-icon
                  v-if="isModalDaySelected(day.value)"
                  icon="CheckCircleIcon"
                  size="28"
                  class="text-success"
                />
                <feather-icon
                  v-else
                  icon="CircleIcon"
                  size="28"
                  class="text-muted"
                />
              </div>
              <div class="day-label">
                {{ day.label }}
              </div>
            </div>
          </div>
        </div>

        <!-- Summary -->
        <div class="mb-4">
          <div
            v-if="modalPaymentDays && modalPaymentDays.length > 0"
            class="selected-summary-modal"
          >
            <feather-icon
              icon="CheckIcon"
              size="14"
              class="mr-1"
            />
            <span class="font-weight-bold">
              {{ modalPaymentDays.length }} jour(s) sélectionné(s)
            </span>
          </div>
          <div
            v-else
            class="text-muted"
          >
            <small>
              <feather-icon
                icon="InfoIcon"
                size="14"
                class="mr-1"
              />
              Aucun jour sélectionné
            </small>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-between">
          <b-button
            variant="outline-secondary"
            @click="closePaymentDaysModal"
          >
            <feather-icon
              icon="XIcon"
              size="16"
              class="mr-50"
            />
            Annuler
          </b-button>
          <b-button
            variant="primary"
            :disabled="isSavingPaymentDays"
            @click="savePaymentDays"
          >
            <b-spinner
              v-if="isSavingPaymentDays"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="SaveIcon"
              size="16"
              class="mr-50"
            />
            Enregistrer
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PasswordGenerator from '@core/components/password/PasswordGenerator.vue'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required, confirmed, min } from 'vee-validate/dist/rules'
import {
  BButton,
  BCard,
  BCardBody,
  BBadge,
  BRow,
  BCol,
  BFormInput,
  BFormGroup,
  BSpinner,
  BButtonGroup,
  BInputGroup,
  BInputGroupAppend,
  BFormDatepicker,
  BFormText,
  BModal,
  BAlert,
  BForm,
  BProgress,
} from 'bootstrap-vue'
import storeAuth from '@/store/store'

// Register validation rules
extend('required', required)
extend('confirmed', confirmed)
extend('min', min)

export default {
  components: {
    vSelect,
    ValidationProvider,
    ValidationObserver,
    PasswordGenerator,
    BButton,
    BCard,
    BCardBody,
    BBadge,
    BRow,
    BCol,
    BFormInput,
    BFormGroup,
    BSpinner,
    BButtonGroup,
    BInputGroup,
    BInputGroupAppend,
    BFormDatepicker,
    BFormText,
    BModal,
    BAlert,
    BForm,
    BProgress,
  },

  data() {
    return {
      columns: [
        {
          label: 'Fournisseur',
          field: 'username',
          sortable: false,
          filterOptions: {
            placeholder: 'Nom',
          },
          width: '200px',
        },
        {
          label: 'Contact',
          field: 'contact_info',
          sortable: false,
          width: '220px',
        },
        {
          label: 'Dépôt',
          field: 'repository',
          sortable: false,
          filterOptions: {
            enabled: true,
            placeholder: 'Dépôt',
          },
          width: '150px',
        },
        {
          label: 'Tarification',
          field: 'pricing_info',
          sortable: false,
          width: '180px',
        },
        {
          label: 'Jour paiement',
          field: 'invoice_generation_days',
          sortable: false,
          width: '210px',
        },
        {
          label: 'Statut',
          field: 'is_active',
          sortable: false,
          width: '120px',
        },
        {
          label: 'Actions',
          field: 'action',
          sortable: false,
          width: '140px',
        },
      ],
      suppliers: [],
      searchTerm: '',
      isLoading: false,
      pageLength: 100,
      currentPage: 1,
      totalPages: 1,
      totalSuppliers: 0,
      viewMode: 'card', // Changed from 'table' to 'card' - default view is now cards
      repositories: [],
      filters: {
        repository: null,
        is_active: null,
        weight_price: null,
        has_payment_days: null,
        min_price_delivery: '',
        max_price_delivery: '',
        min_price_return: '',
        max_price_return: '',
        created_at_after: '',
        created_at_before: '',
      },
      showAdvancedFilters: false,
      summaryStats: {
        total: 0,
        active: 0,
        inactive: 0,
        orders: 0,
        weightPricing: 0,
      },
      weekdayLabels: {
        monday: 'Lundi',
        tuesday: 'Mardi',
        wednesday: 'Mercredi',
        thursday: 'Jeudi',
        friday: 'Vendredi',
        saturday: 'Samedi',
        sunday: 'Dimanche',
      },
      weekdayShortLabels: {
        monday: 'Lun',
        tuesday: 'Mar',
        wednesday: 'Mer',
        thursday: 'Jeu',
        friday: 'Ven',
        saturday: 'Sam',
        sunday: 'Dim',
      },
      weekdayOptions: [
        { label: 'Lundi', value: 'monday' },
        { label: 'Mardi', value: 'tuesday' },
        { label: 'Mercredi', value: 'wednesday' },
        { label: 'Jeudi', value: 'thursday' },
        { label: 'Vendredi', value: 'friday' },
        { label: 'Samedi', value: 'saturday' },
        { label: 'Dimanche', value: 'sunday' },
      ],
      searchTimer: null,
      user: {},
      showPaymentDaysModal: false,
      selectedSupplier: null,
      modalPaymentDays: [],
      isSavingPaymentDays: false,
      // Password change modal state
      showPasswordChangeModal: false,
      selectedSupplierForPassword: null,
      modalPasswordForm: {
        password: '',
        confirm_password: '',
      },
      showModalPassword: false,
      showModalConfirmPassword: false,
      isSavingPassword: false,
    }
  },

  created() {
    this.user = storeAuth.state.user
    // Allowed roles (superadmin has read-only access)
    const allowedRoles = ['admin', 'accountant', 'manager', 'warehouse_operator', 'sales', 'superadmin']
    if (!allowedRoles.includes(this.user.role)) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Accès refusé',
          icon: 'AlertCircleIcon',
          text: "Vous n'avez pas la permission d'accéder à cette page.",
          variant: 'danger',
        },
      }, { position: 'top-center' })
      this.$router.replace({ name: 'home' })
      return
    }
    this.getSuppliers()
    if (this.user.role === 'admin') {
      this.fetchRepositories()
    }
  },

  computed: {
    passwordStrength() {
      let strength = 0
      const { password } = this.modalPasswordForm

      if (!password) return 0

      // Length contribution
      if (password.length >= 8) strength += 25

      // Character type contribution
      if (/[A-Z]/.test(password)) strength += 25 // Uppercase
      if (/[0-9]/.test(password)) strength += 25 // Numbers
      if (/[^A-Za-z0-9]/.test(password)) strength += 25 // Special chars

      return strength
    },

    passwordStrengthClass() {
      if (this.passwordStrength <= 25) return 'danger'
      if (this.passwordStrength <= 50) return 'warning'
      if (this.passwordStrength <= 75) return 'info'
      return 'success'
    },

    passwordStrengthText() {
      if (this.passwordStrength <= 25) return 'Faible'
      if (this.passwordStrength <= 50) return 'Moyen'
      if (this.passwordStrength <= 75) return 'Bon'
      return 'Fort'
    },

    passwordsMatch() {
      return (
        this.modalPasswordForm.password
        && this.modalPasswordForm.confirm_password
        && this.modalPasswordForm.password === this.modalPasswordForm.confirm_password
      )
    },

    canSavePassword() {
      return (
        this.modalPasswordForm.password
        && this.modalPasswordForm.confirm_password
        && this.passwordsMatch
        && this.modalPasswordForm.password.length >= 8
      )
    },
  },

  methods: {
    async getSuppliers() {
      this.isLoading = true
      try {
        // Only allow for permitted roles (including superadmin for read-only listing)
        const allowedRoles = ['admin', 'accountant', 'manager', 'warehouse_operator', 'sales', 'superadmin']
        if (!allowedRoles.includes(this.user.role)) {
          this.suppliers = []
          this.isLoading = false
          return
        }

        // Build params object for API call
        const params = {}
        if (this.filters.repository && this.user.role === 'admin') {
          params.repository = this.filters.repository
        }
        if (this.filters.is_active !== null && this.filters.is_active !== undefined) {
          params.is_active = this.filters.is_active
        }
        if (this.filters.weight_price !== null && this.filters.weight_price !== undefined) {
          params.weight_price = this.filters.weight_price
        }
        if (this.filters.has_payment_days !== null && this.filters.has_payment_days !== undefined) {
          params.has_payment_days = this.filters.has_payment_days
        }
        if (this.filters.min_price_delivery) {
          params.min_price_delivery = this.filters.min_price_delivery
        }
        if (this.filters.max_price_delivery) {
          params.max_price_delivery = this.filters.max_price_delivery
        }
        if (this.filters.min_price_return) {
          params.min_price_return = this.filters.min_price_return
        }
        if (this.filters.max_price_return) {
          params.max_price_return = this.filters.max_price_return
        }
        if (this.filters.created_at_after) {
          params.created_at_after = this.filters.created_at_after
        }
        if (this.filters.created_at_before) {
          params.created_at_before = this.filters.created_at_before
        }
        if (this.searchTerm) {
          params.search = this.searchTerm
        }

        // Add pagination params
        params.page = this.currentPage
        params.page_size = this.pageLength

        const { data } = await axios.get('/api/auth/users/suppliers/all/', { params })
        // Use paginated response
        this.suppliers = Array.isArray(data.results) ? data.results : []
        this.totalSuppliers = data.count || this.suppliers.length
        this.currentPage = data.current_page || 1
        this.totalPages = data.total_pages || 1
        this.calculateSummaryStats()
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des expéditeurs')
      } finally {
        this.isLoading = false
      }
    },

    // Unified pagination handler (like orders list)
    async pageNext(page) {
      this.currentPage = page
      await this.getSuppliers()
    },

    // Handler for page size change
    async onPageLengthChange() {
      this.currentPage = 1
      await this.getSuppliers()
    },

    async fetchRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des dépôts')
      }
    },

    calculateSummaryStats() {
      // Make sure suppliers is an array before using array methods
      if (!Array.isArray(this.suppliers)) {
        this.summaryStats = {
          total: 0,
          active: 0,
          inactive: 0,
          orders: 0,
          weightPricing: 0,
        }
        return
      }

      this.summaryStats = {
        total: this.totalSuppliers,
        active: this.suppliers.filter(s => s.is_active).length,
        inactive: this.suppliers.filter(s => !s.is_active).length,
        orders: this.suppliers.reduce((sum, supplier) => sum + (supplier.active_orders_count || 0), 0),
        weightPricing: this.suppliers.filter(s => s.weight_price).length,
      }
    },

    async toggleSupplierStatus(supplier) {
      const action = supplier.is_active ? 'désactiver' : 'activer'
      const actionText = supplier.is_active ? 'désactivé' : 'activé'

      try {
        const result = await this.$swal({
          title: `${action.charAt(0).toUpperCase() + action.slice(1)} le fournisseur ?`,
          text: `Êtes-vous sûr de vouloir ${action} ${supplier.username} ?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: `Oui, ${action}`,
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: `btn ${supplier.is_active ? 'btn-warning' : 'btn-success'}`,
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })
        if (result.value) {
          this.isLoading = true
          try {
            await axios.put(`/api/auth/users/delete/${supplier.id}/`)
            await this.getSuppliers()
            this.showToast('success', 'top-center', `Fournisseur ${actionText} avec succès`)
          } catch (error) {
            this.showToast('danger', 'top-center', `Erreur lors de la modification du statut: ${error.response?.data?.message || error.message}`)
          } finally {
            this.isLoading = false
          }
        }
      } catch (e) {
        // Modal dismissed, do nothing
      }
    },

    async confirmSafeDelete(supplier) {
      try {
        const result = await this.$swal({
          title: 'Supprimer le fournisseur ?',
          text: `Êtes-vous sûr de vouloir supprimer ${supplier.username || 'ce fournisseur'} ?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Oui, supprimer',
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })

        if (result.value) {
          this.isLoading = true
          try {
            await axios.delete(`/api/auth/users/suppliers/${supplier.id}/`)
            await this.getSuppliers()
            this.showToast('success', 'top-center', `Le fournisseur ${supplier.username} a été supprimé avec succès`)
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.response?.data?.message || error.response?.data?.detail || error.message
            this.showToast('danger', 'top-center', errorMsg)
          } finally {
            this.isLoading = false
          }
        }
      } catch (e) {
        // Modal dismissed
      }
    },

    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: variant === 'success' ? 'Succès' : 'Erreur',
            icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
            text,
            variant,
          },
        },
        { position },
      )
    },

    toggleViewMode() {
      this.viewMode = this.viewMode === 'table' ? 'card' : 'table'
    },

    resetFilters() {
      this.searchTerm = ''
      this.filters = {
        repository: null,
        is_active: null,
        weight_price: null,
        has_payment_days: null,
        min_price_delivery: '',
        max_price_delivery: '',
        min_price_return: '',
        max_price_return: '',
        created_at_after: '',
        created_at_before: '',
      }
      this.getSuppliers()
    },

    toggleAdvancedFilters() {
      this.showAdvancedFilters = !this.showAdvancedFilters
    },

    onSearchChange() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.getSuppliers()
      }, 500)
    },

    getInitials(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },

    getInvoiceGenerationDays(supplier) {
      if (!supplier || !Array.isArray(supplier.invoice_generation_days)) {
        return []
      }

      return supplier.invoice_generation_days.filter(day => this.weekdayLabels[day])
    },

    getDayLabel(day) {
      return this.weekdayLabels[day] || day
    },

    getShortDayLabel(day) {
      return this.weekdayShortLabels[day] || day
    },

    getDayChipClass(day) {
      return `invoice-day-chip--${day}`
    },

    formatInvoiceGenerationDays(supplier) {
      const days = this.getInvoiceGenerationDays(supplier)

      if (!days.length) {
        return 'Non planifiée'
      }

      return days.map(day => this.getDayLabel(day)).join(' / ')
    },

    exportToCSV() {
      try {
        // Format data for CSV
        const csvData = this.suppliers.map(supplier => ({
          Nom: supplier.username || '',
          Email: supplier.email || '',
          Téléphone: supplier.phone || '',
          Dépôt: supplier.repository ? supplier.repository.name : '',
          'Jour paiement': this.formatInvoiceGenerationDays(supplier),
          'Prix par poids': supplier.weight_price ? 'Oui' : 'Non',
          Statut: supplier.is_active ? 'Actif' : 'Inactif',
        }))

        // Convert to CSV string
        const headers = Object.keys(csvData[0]).join(',')
        const rows = csvData.map(obj => Object.values(obj).map(val => `"${val}"`).join(','))
        const csvContent = [headers, ...rows].join('\n')

        // Create blob and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)

        link.setAttribute('href', url)
        link.setAttribute('download', `expéditeurs_${new Date().toISOString().slice(0, 10)}.csv`)
        link.style.visibility = 'hidden'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.showToast('success', 'top-right', 'Données exportées avec succès')
      } catch (error) {
        this.showToast('danger', 'top-right', "Erreur lors de l'exportation")
      }
    },

    // Quick Edit Payment Days Modal Methods
    openPaymentDaysModal(supplier) {
      this.selectedSupplier = supplier
      this.modalPaymentDays = supplier.invoice_generation_days ? [...supplier.invoice_generation_days] : []
      this.showPaymentDaysModal = true
    },

    closePaymentDaysModal() {
      this.showPaymentDaysModal = false
    },

    resetPaymentDaysModal() {
      this.selectedSupplier = null
      this.modalPaymentDays = []
      this.isSavingPaymentDays = false
    },

    isModalDaySelected(dayValue) {
      return this.modalPaymentDays && this.modalPaymentDays.includes(dayValue)
    },

    toggleModalDay(dayValue) {
      if (!this.modalPaymentDays) {
        this.modalPaymentDays = []
      }
      const index = this.modalPaymentDays.indexOf(dayValue)
      if (index > -1) {
        this.modalPaymentDays.splice(index, 1)
      } else {
        this.modalPaymentDays.push(dayValue)
      }
    },

    async savePaymentDays() {
      if (!this.selectedSupplier) return

      this.isSavingPaymentDays = true
      try {
        await axios.patch(`/api/auth/users/update/${this.selectedSupplier.id}/`, {
          invoice_generation_days: this.modalPaymentDays,
        })

        // Update the supplier in the list
        const supplierIndex = this.suppliers.findIndex(s => s.id === this.selectedSupplier.id)
        if (supplierIndex > -1) {
          this.suppliers[supplierIndex].invoice_generation_days = [...this.modalPaymentDays]
        }

        this.showToast('success', 'top-right', 'Jours de paiement mis à jour avec succès')
        this.closePaymentDaysModal()
      } catch (error) {
        const errorMsg = error.response?.data?.message || error.response?.data?.detail || 'Erreur lors de la mise à jour'
        this.showToast('danger', 'top-right', errorMsg)
      } finally {
        this.isSavingPaymentDays = false
      }
    },

    // Password Change Modal Methods
    openPasswordChangeModal(supplier) {
      this.selectedSupplierForPassword = supplier
      this.modalPasswordForm = {
        password: '',
        confirm_password: '',
      }
      this.showModalPassword = false
      this.showModalConfirmPassword = false
      this.showPasswordChangeModal = true
    },

    closePasswordChangeModal() {
      this.showPasswordChangeModal = false
    },

    resetPasswordChangeModal() {
      this.selectedSupplierForPassword = null
      this.modalPasswordForm = {
        password: '',
        confirm_password: '',
      }
      this.showModalPassword = false
      this.showModalConfirmPassword = false
      this.isSavingPassword = false

      // Reset validation
      if (this.$refs.passwordFormValidator) {
        this.$refs.passwordFormValidator.reset()
      }
    },

    onPasswordGenerated(generatedPassword) {
      this.modalPasswordForm.password = generatedPassword
      this.modalPasswordForm.confirm_password = generatedPassword
      this.showModalPassword = true
      this.showModalConfirmPassword = true
      this.$forceUpdate()
    },

    onPasswordInput() {
      // Trigger real-time validation
      this.$forceUpdate()
    },

    getConfirmPasswordState(errors) {
      if (errors.length > 0) return false

      // Show validation state only if user has typed in confirm field
      if (!this.modalPasswordForm.confirm_password) return null

      // Real-time match validation
      return !!this.passwordsMatch
    },

    async savePasswordChange() {
      if (!this.canSavePassword || !this.selectedSupplierForPassword) return

      this.isSavingPassword = true
      try {
        await axios.post(
          `/api/auth/users/suppliers/change-password/${this.selectedSupplierForPassword.id}/`,
          this.modalPasswordForm,
        )

        this.showToast('success', 'top-right', 'Mot de passe modifié avec succès')
        this.closePasswordChangeModal()
      } catch (error) {
        const errorMsg = error.response?.data?.error
          || error.response?.data?.detail
          || 'Erreur lors du changement de mot de passe'
        this.showToast('danger', 'top-right', errorMsg)
      } finally {
        this.isSavingPassword = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";
@import "@core/scss/vue/libs/vue-select.scss";
@import "@/assets/scss/user-modules.scss";

.supplier-card {
  @extend .user-card;

  transition: all 0.3s ease;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
  }

  &.border-success {
    border-color: rgba(40, 167, 69, 0.3) !important;

    &:hover {
      border-color: rgba(40, 167, 69, 0.6) !important;
    }
  }

  &.border-secondary {
    border-color: rgba(108, 117, 125, 0.3) !important;

    &:hover {
      border-color: rgba(108, 117, 125, 0.6) !important;
    }
  }
}

/* Enhanced styling for v-select */
.enhanced-select {
  .vs__dropdown-toggle {
    padding: 4px 8px;
    border: 1px solid #d8d6de !important;
    border-radius: 0.357rem;
    background-color: #f8f8f8;
    transition: all 0.3s ease-in-out;
    min-height: 38px;

    &:hover {
      border-color: rgba(234, 84, 85, 0.75) !important;
    }
  }

  .vs__dropdown-toggle:focus-within {
    border-color: rgba(234, 84, 85, 0.75) !important;
    box-shadow: 0 3px 10px 0 rgba(234, 84, 85, 0.15);
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: rgba(234, 84, 85, 0.75) !important;
    box-shadow: 0 3px 10px 0 rgba(234, 84, 85, 0.15);
  }

  .vs__selected {
    margin: 0 2px;
    padding: 0 5px;
    color: #e0111a;
    font-weight: 700;
  }

  .vs__selected-options {
    padding: 2px;
  }

  .vs__search::placeholder {
    color: #b9b9c3;
  }

  .vs__actions {
    padding-right: 8px;
  }

  .vs__dropdown-menu {
    padding: 0;
    border: 1px solid #d8d6de;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }

  .vs__dropdown-option {
    padding: 8px 20px;

    &--selected {
      color: #e0111a;
      font-weight: 700;
    }

    &:hover, &--highlight {
      background: rgba(234, 84, 85, 0.08);
      color: #e0111a;
    }
  }
}

/* Specific override for border visibility */
.border-select .vs__dropdown-toggle {
  border: 1px solid #d8d6de !important;
}

/* Enhanced Table Styling */
.vgt-table {
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.1);

  .vgt-table-header {
    background: linear-gradient(135deg, #ff0000 0%, #0a3370 100%);

    th {
      color: white !important;
      font-weight: 600 !important;
      text-transform: uppercase;
      font-size: 0.75rem;
      letter-spacing: 0.5px;
      border: none !important;
      padding: 1rem 0.75rem;

      &:first-child {
        border-top-left-radius: 0.5rem;
      }

      &:last-child {
        border-top-right-radius: 0.5rem;
      }
    }
  }

  tbody {
    tr {
      transition: all 0.3s ease;

      &:hover {
        background-color: #f8f9fa !important;
        transform: translateY(-1px);
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
      }

      td {
        padding: 1rem 0.75rem;
        vertical-align: middle;
        border-bottom: 1px solid #ede7f6;
      }
    }
  }
}

/* Enhanced Action Buttons */
.action-buttons {
  .btn-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    }
  }
}

/* Avatar enhancements */
.avatar-wrapper {
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  .status-indicator {
    animation: pulse 2s infinite;
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(40, 167, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
  }
}

/* Contact info styling */
.contact-info {
  font-size: 0.875rem;

  .feather-icon {
    opacity: 0.7;
  }
}

/* Pricing info styling */
.pricing-info {
  font-size: 0.8rem;

  .badge {
    font-size: 0.7rem;
    min-width: 60px;
  }
}

/* Enhanced badges */
.badge {
  &.badge-pill {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    font-weight: 600;
  }
}

.supplier-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.65rem !important;
  font-size: 0.85rem !important;
  font-weight: 800 !important;
  line-height: 1 !important;
  border-radius: 999px;

  .feather-icon {
    margin-right: 0.35rem !important;
  }
}

/* Summary cards enhancements */
.card {
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
  }
}

.avatar {
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
}

/* Enhanced search and filter area */
.card .row {
  .form-group {
    label {
      font-weight: 600;
      color: #5e5873;
      margin-bottom: 0.5rem;
    }
  }
}

/* Responsive enhancements */
@media (max-width: 768px) {
  .vgt-table {
    font-size: 0.8rem;

    .action-buttons {
      .btn-icon {
        width: 28px;
        height: 28px;
      }
    }
  }

  .avatar-wrapper {
    width: 36px !important;
    height: 36px !important;
    min-width: 36px !important;
  }
}

/* Loading state improvements */
.spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Enhanced empty state */
.empty-state-icon {
  .feather-icon {
    animation: float 3s ease-in-out infinite;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

/* Enhanced Payment Days Styling */
.invoice-days-summary {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem;

  .invoice-days-title {
    font-size: 0.75rem;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
  }

  .invoice-days-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  .invoice-day-chip {
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.6rem;
    background: linear-gradient(135deg, #e2eefa 0%, #f2f7fc 100%);
    border: 1px solid #ff0000;
    border-radius: 6px;
    color: #ff0000;
    font-size: 0.75rem;
    font-weight: 700;
    transition: all 0.2s ease;

    &:hover {
      background: #ff0000;
      color: #ffffff;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(13, 67, 153, 0.25);
    }

    .feather-icon {
      color: currentColor;
    }
  }

  .invoice-days-empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem 0;
  }

  .payment-days-warning {
    display: inline-flex;
    align-items: center;
    padding: 0.4rem 0.75rem !important;
    font-size: 0.75rem !important;
    font-weight: 700 !important;
    background: #fff3cd !important;
    color: #856404 !important;
    border: 1px solid #ffc107;
    animation: pulse-warning 2s ease-in-out infinite;

    .feather-icon {
      color: #856404;
    }
  }
}

@keyframes pulse-warning {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(255, 193, 7, 0);
  }
}

/* Payment Days Quick Edit Modal */
#payment-days-modal {
  .modal-header {
    border-bottom: 1px solid #e2e8f0;
    padding: 1.5rem;
  }

  .modal-body {
    padding: 1.5rem;
  }

  .payment-icon-wrapper-modal {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #e2eefa 0%, #f2f7fc 100%);
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(13, 67, 153, 0.1);
  }

  .payment-days-section-modal {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 1.25rem;
  }

  .days-picker-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
    gap: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .day-picker-item {
    background: #ffffff;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    padding: 1.25rem 0.75rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;

    &:hover {
      border-color: #ff0000;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(13, 67, 153, 0.15);
    }

    &.selected {
      background: linear-gradient(135deg, #e2eefa 0%, #f2f7fc 100%);
      border-color: #ff0000;
      border-width: 2px;

      .day-label {
        color: #ff0000;
        font-weight: 700;
      }

      &:hover {
        box-shadow: 0 6px 16px rgba(13, 67, 153, 0.25);
      }
    }

    .day-icon {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 36px;
    }

    .day-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #64748b;
      transition: all 0.2s ease;
    }
  }

  .selected-summary-modal {
    background: #f0fdf4;
    border: 1px solid #86efac;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    display: inline-flex;
    align-items: center;
    color: #16a34a;
  }
}

/* Password Change Modal Premium Styling */
#password-change-modal {
  ::v-deep .modal-content {
    border-radius: 16px;
    border: none;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  }

  ::v-deep .modal-header {
    border-bottom: 1px solid #e2e8f0;
    padding: 1.5rem;
  }

  ::v-deep .modal-body {
    padding: 1.5rem;
  }

  .password-icon-wrapper-modal {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #e2eefa 0%, #f2f7fc 100%);
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(13, 67, 153, 0.1);
  }

  .form-label-premium {
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .generate-password-btn-centered {
    padding: 0.75rem 2rem;
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.9375rem;
    border: 2px solid #ff0000;
    color: #ff0000;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(13, 67, 153, 0.1);

    &:hover {
      background: linear-gradient(135deg, #ff0000 0%, #0a3370 100%);
      color: #ffffff;
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(13, 67, 153, 0.3);
      border-color: #ff0000;
    }

    &:active {
      transform: translateY(0);
    }
  }

  .input-with-button-wrapper {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;

    .premium-input-group {
      flex: 1;
    }

    .generate-password-btn {
      padding: 0.65rem 1.25rem;
      border-radius: 8px;
      font-weight: 600;
      font-size: 0.875rem;
      border: 1px solid #ff0000;
      color: #ff0000;
      background: #ffffff;
      transition: all 0.2s ease;
      white-space: nowrap;

      &:hover {
        background: #ff0000;
        color: #ffffff;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(13, 67, 153, 0.25);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  .premium-input-group {
    .form-control {
      border: 1px solid #e2e8f0;
      padding: 0.75rem 1rem;
      border-radius: 8px 0 0 8px;
      font-size: 0.9375rem;
      transition: all 0.2s ease;

      &:focus {
        border-color: #ff0000;
        box-shadow: 0 0 0 3px rgba(13, 67, 153, 0.1);
      }

      &.is-valid {
        border-color: #28a745;

        &:focus {
          box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.15);
        }
      }

      &.is-invalid {
        border-color: #dc3545;

        &:focus {
          box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.15);
        }
      }
    }

    .input-group-append {
      .password-toggle-btn {
        border: 1px solid #e2e8f0;
        border-left: none;
        border-radius: 0 8px 8px 0;
        background: #f8fafc;
        color: #64748b;
        padding: 0.75rem 1rem;
        transition: all 0.2s ease;

        &:hover {
          background: #ff0000;
          color: #ffffff;
          border-color: #ff0000;
        }
      }
    }
  }

  .password-strength-wrapper {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.75rem;

    .strength-label {
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      font-size: 0.7rem;
    }

    .strength-text {
      font-weight: 700;
      font-size: 0.75rem;

      &.text-danger {
        color: #dc3545 !important;
      }

      &.text-warning {
        color: #ffc107 !important;
      }

      &.text-info {
        color: #17a2b8 !important;
      }

      &.text-success {
        color: #28a745 !important;
      }
    }

    .premium-progress {
      border-radius: 10px;
      background: #e2e8f0;
      overflow: hidden;

      ::v-deep .progress-bar {
        border-radius: 10px;
        transition: width 0.3s ease;
      }
    }
  }

  .premium-btn-secondary {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    border: 1px solid #e2e8f0;
    color: #64748b;
    background: #ffffff;
    transition: all 0.2s ease;

    &:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
      color: #475569;
      transform: translateY(-1px);
    }
  }

  .premium-btn-primary {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    background: linear-gradient(135deg, #ff0000 0%, #0a3370 100%);
    border: none;
    color: #ffffff;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(13, 67, 153, 0.3);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &.not-allowed,
    &:disabled {
      opacity: 0.6 !important;
      cursor: not-allowed !important;
      pointer-events: auto !important;
    }

    &.btn-saving {
      animation: pulse-save 1.5s ease-in-out infinite;
    }
  }
}

@keyframes pulse-save {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(13, 67, 153, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(13, 67, 153, 0);
  }
}

/* Validation message animation */
.validation-message-animate {
  animation: fadeInSlide 0.3s ease-out;
}

@keyframes fadeInSlide {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
